/*
  FC Product Grid — behaviour for the "FC Grid" gallery layout (gallery_layout: fc_grid)
  <fc-load-more>    reveals media items beyond the configured limit (desktop grid).
  <fc-gallery-dots> mobile slider indicator, driven by product-gallery's slideChanged event.
  Loaded by sections/main-product.liquid only when the layout is active.
  Developer: Alfie
  Updated:   2026-08-20
*/

if (!customElements.get('fc-load-more')) {
  customElements.define('fc-load-more', class FcLoadMore extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.mediaGallery = this.closest('media-gallery');
      if (!this.button || !this.mediaGallery) return;

      this.recount();
      this.button.addEventListener('click', this.onButtonClick.bind(this));

      // Variant changes with hide_variants prepend the active media item
      // (media-gallery.js), shifting which items sit past the limit.
      const mediaList = this.mediaGallery.querySelector('.product__media-list');
      if (mediaList) {
        new MutationObserver(() => {
          if (!this.mediaGallery.classList.contains('fc-grid-expanded')) this.recount();
        }).observe(mediaList, { childList: true });
      }
    }

    // Liquid tags items past the limit by DOM order; media hidden by the
    // hide-variants/gang rules would silently eat visible slots, so re-tag
    // against the items that are actually rendered.
    recount() {
      const limit = parseInt(this.dataset.limit, 10) || 6;
      const items = Array.from(this.mediaGallery.querySelectorAll('.product__media-item'));

      items.forEach((item) => item.classList.remove('fc-grid-item--extra'));
      const visibleItems = items.filter((item) => getComputedStyle(item).display !== 'none');
      visibleItems.slice(limit).forEach((item) => item.classList.add('fc-grid-item--extra'));

      this.hidden = visibleItems.length <= limit;
    }

    onButtonClick() {
      const firstRevealed = this.mediaGallery.querySelector('.fc-grid-item--extra');

      this.mediaGallery.classList.add('fc-grid-expanded');
      this.button.setAttribute('aria-expanded', 'true');
      this.hidden = true;

      if (firstRevealed) {
        firstRevealed.setAttribute('tabindex', '-1');
        firstRevealed.focus();
      }
    }
  });
}

if (!customElements.get('fc-gallery-dots')) {
  customElements.define('fc-gallery-dots', class FcGalleryDots extends HTMLElement {
    connectedCallback() {
      this.gallery = this.closest('product-gallery');
      if (!this.gallery) return;

      this.sync();
      this.gallery.addEventListener('slideChanged', this.onSlideChanged.bind(this));

      // Re-sync when variant changes reorder or swap the visible slides
      const mediaList = this.gallery.querySelector('.product__media-list');
      if (mediaList) {
        new MutationObserver(() => this.sync()).observe(mediaList, { childList: true });
      }
    }

    visibleDots() {
      return Array.from(this.querySelectorAll('.fc-gallery-dots__dot')).filter((dot) => !dot.hidden);
    }

    // Hide dots whose slide is hidden (hide-variants/gang media) so the dot
    // count always matches the real number of slides.
    sync() {
      this.querySelectorAll('.fc-gallery-dots__dot').forEach((dot) => {
        const slide = this.gallery.querySelector(`[data-media-id="${dot.dataset.target}"]`);
        dot.hidden = !slide || getComputedStyle(slide).display === 'none';
      });
      this.hidden = this.visibleDots().length < 2;
    }

    onSlideChanged(event) {
      const currentSlide = event.detail.currentElement;
      if (!currentSlide) return;

      this.visibleDots().forEach((dot) => {
        dot.classList.toggle('is-active', dot.dataset.target === currentSlide.dataset.mediaId);
      });
    }
  });
}
